import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { base64Decode } from '@app/helpers/base64url';
import {
	getAllTemplates,
	getTemplatesByFranchise,
	deleteTemplate,
	toggleTemplateStatus,
	duplicateTemplate,
	getTemplateTypes,
	getTemplateHistory,
	ContentTemplate
} from '../../../services/contentTemplates';
import ContentTemplateEditor from './ContentTemplateEditor';

const ManageContentTemplates = () => {
	const permissions = useSelector((state: any) => state.auth.permissions?.['content-templates']);
	const location = useLocation();
	const navigate = useNavigate();
	const { encodedData } = useParams<{ encodedData?: string }>();
	
	let franchiseId = null;
	let franchiseName = '';
	
	if (encodedData) {
		try {
			const decoded = JSON.parse(base64Decode(encodedData));
			franchiseId = decoded.id?.toString() || null;
			franchiseName = decoded.name || '';
		} catch (error) {
			console.error('Error decoding franchise data:', error);
		}
	}

	const [templates, setTemplates] = useState<ContentTemplate[]>([]);
	const [loading, setLoading] = useState(false);
	const [showEditor, setShowEditor] = useState(false);
	const [showHistory, setShowHistory] = useState(false);
	const [historyData, setHistoryData] = useState<any[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<ContentTemplate | undefined>();
	const [editMode, setEditMode] = useState<'content' | 'values' | 'preview' | null>(null);
	const [returnToHistory, setReturnToHistory] = useState(false);

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [itemsPerPage] = useState(10);

	// Filters
	const [filterType, setFilterType] = useState<string>('');
	const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
	const [filterTitle, setFilterTitle] = useState<string>('');
	const [templateTypes, setTemplateTypes] = useState<Array<{ id: number; name: string; slug: string }>>([]);

	useEffect(() => {
		fetchTemplates();
	}, [currentPage, filterType, filterActive, filterTitle, franchiseId]);

	useEffect(() => {
		fetchTemplateTypes();
	}, []);

	const fetchTemplateTypes = async () => {
		try {
			const response = await getTemplateTypes();
			if (response?.success && response?.collections) {
				setTemplateTypes(response.collections);
			}
		} catch (error) {
			console.error('Error fetching template types:', error);
		}
	};

	const fetchTemplates = async () => {
		setLoading(true);
		try {
			let response;
			
			if (franchiseId) {
				// Fetch templates for specific franchise
				response = await getTemplatesByFranchise(
					parseInt(franchiseId),
					currentPage,
					itemsPerPage,
					filterType,
					filterActive,
					filterTitle
				);
			} else {
				// Fetch all templates
				response = await getAllTemplates(
					currentPage,
					itemsPerPage,
					filterType,
					filterActive,
					filterTitle
				);
			}

			if (response?.success) {
				setTemplates(response.collections?.items || response.collections?.data || []);
				setTotalItems(response.collections?.total || 0);
			} else {
				toast.error(response?.message || 'Failed to fetch templates');
				setTemplates([]);
				setTotalItems(0);
			}
		} catch (error) {
			console.error('Error fetching templates:', error);
			toast.error('An error occurred while fetching templates');
			setTemplates([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setSelectedTemplate(undefined);
		setEditMode('content');
		setShowEditor(true);
	};

	const handleEdit = (template: ContentTemplate, mode: 'content' | 'values') => {
		setSelectedTemplate(template);
		setEditMode(mode);
		setShowEditor(true);
	};

	const handlePreview = (template: ContentTemplate) => {
		setSelectedTemplate(template);
		setEditMode('preview');
		setShowEditor(true);
	};

	const handleViewHistory = async (template: ContentTemplate) => {
		setLoading(true);
		try {
			const response = await getTemplateHistory(
				franchiseId ? parseInt(franchiseId) : undefined,
				template.consent_template_type_id
			);
			
			if (response?.success) {
				setHistoryData(response.collections || []);
				setSelectedTemplate(template);
				setShowHistory(true);
			} else {
				toast.error(response?.message || 'Failed to fetch history');
			}
		} catch (error) {
			console.error('Error fetching history:', error);
			toast.error('An error occurred while fetching history');
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this template?')) {
			return;
		}

		try {
			const response = await deleteTemplate(id);
			if (response.success) {
				toast.success('Template deleted successfully');
				fetchTemplates();
			} else {
				toast.error(response.message || 'Failed to delete template');
			}
		} catch (error) {
			console.error('Error deleting template:', error);
			toast.error('An error occurred while deleting template');
		}
	};

	const handleToggleStatus = async (id: number, currentStatus: boolean) => {
		try {
			const response = await toggleTemplateStatus(id, !currentStatus);
			if (response.success) {
				toast.success(`Template ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
				fetchTemplates();
			} else {
				toast.error(response.message || 'Failed to update template status');
			}
		} catch (error) {
			console.error('Error toggling template status:', error);
			toast.error('An error occurred while updating template status');
		}
	};

	const handleDuplicate = async (id: number, title: string) => {
		const newTitle = prompt('Enter title for the duplicated template:', `${title} (Copy)`);
		if (!newTitle) return;

		try {
			const response = await duplicateTemplate(id, newTitle);
			if (response.success) {
				toast.success('Template duplicated successfully');
				fetchTemplates();
			} else {
				toast.error(response.message || 'Failed to duplicate template');
			}
		} catch (error) {
			console.error('Error duplicating template:', error);
			toast.error('An error occurred while duplicating template');
		}
	};

	const handleSaveTemplate = async () => {
		setShowEditor(false);
		setSelectedTemplate(undefined);
		setEditMode(null);
		setReturnToHistory(false);
		fetchTemplates();
	};

	const handleCancelEditor = () => {
		if (returnToHistory) {
			setShowEditor(false);
			setShowHistory(true);
			setReturnToHistory(false);
		} else {
			setShowEditor(false);
			setSelectedTemplate(undefined);
			setEditMode(null);
		}
	};

	const handleResetFilters = () => {
		setFilterTitle('');
		setFilterType('');
		setFilterActive(undefined);
		setCurrentPage(1);
	};

	const totalPages = Math.ceil(totalItems / itemsPerPage);

	const getTypeBadgeColor = (type: string) => {
		const colors: Record<string, string> = {
			terms_and_conditions: 'primary',
			media_consent: 'success'
		};
		return colors[type] || 'secondary';
	};

	// Check if user has permission to view templates
	if (!permissions?.['list-templates']) {
		return (
			<div className="content">
				<div className="container-fluid">
					<div className="alert alert-warning">
						<i className="fas fa-exclamation-triangle mr-2"></i>
						You do not have permission to view content templates.
					</div>
				</div>
			</div>
		);
	}

	if (showEditor) {
		return (
			<ContentTemplateEditor
				template={selectedTemplate}
				editMode={editMode || 'content'}
				franchiseId={franchiseId ? parseInt(franchiseId) : undefined}
				onSave={handleSaveTemplate}
				onCancel={handleCancelEditor}
			/>
		);
	}

	if (showHistory) {
		return (
			<div className="content-templates-history">
				<div className="content-header">
					<div className="container-fluid">
						<div className="row mb-2">
							<div className="col-sm-6">
								<h1>Template History - {selectedTemplate?.title}</h1>
							</div>
							<div className="col-sm-6">
								<div className="float-sm-right">
									<button
										type="button"
										className="btn btn-secondary"
										onClick={() => {
											setShowHistory(false);
											setHistoryData([]);
											setSelectedTemplate(undefined);
										}}
									>
										<i className="fas fa-arrow-left mr-2"></i>
										Back to Templates
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<section className="content">
					<div className="container-fluid">
						<div className="card">
							<div className="card-header">
								<h3 className="card-title">History Records</h3>
							</div>
							<div className="card-body">
								{historyData.length === 0 ? (
									<div className="text-center p-4">
										<p className="text-muted">No history records found</p>
									</div>
								) : (
									<div className="table-responsive">
										<table className="table table-hover">
											<thead>
												<tr>
													<th>Version</th>
													<th>Title</th>
													<th>Status</th>
													<th>Updated By</th>
													<th>Updated At</th>
													<th style={{ width: '100px' }}>Actions</th>
												</tr>
											</thead>
											<tbody>
												{historyData.map((record, index) => (
													<tr key={index}>
														<td>{record.version}</td>
														<td>{record.title}</td>
														<td>
															<span className={`badge badge-${record.is_active ? 'success' : 'secondary'}`}>
																{record.is_active ? 'Active' : 'Inactive'}
															</span>
														</td>
														<td>{record.updated_by_name || '-'}</td>
														<td>
															{record.updated_at
																? moment(record.updated_at).format('DD/MM/YYYY HH:mm')
																: '-'}
														</td>
														<td>
															<button
																type="button"
																className="btn btn-sm btn-secondary"
																onClick={() => {
																	setSelectedTemplate(record);
																	setEditMode('preview');
																	setReturnToHistory(true);
																	setShowHistory(false);
																	setShowEditor(true);
																}}
																title="Preview"
															>
																<i className="fas fa-eye"></i>
															</button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	return (
		<div className="manage-terms-conditions-templates">
			<div className="content-header">
				<div className="container-fluid">
					<div className="row mb-2">
						<div className="col-sm-6">
							<h1>
								Content Templates
								{franchiseName && (
									<small className="ml-2 text-muted">
										- {franchiseName}
									</small>
								)}
							</h1>
						</div>
						<div className="col-sm-6">
							<div className="float-sm-right">
								<button
									type="button"
									className="btn btn-secondary"
									onClick={() => navigate('/franchise')}
								>
									<i className="fas fa-arrow-left mr-2"></i>
									Back to Franchise List
								</button>
								{/* {permissions?.['create-templates'] && (
									<button
										type="button"
										className="btn btn-primary"
										onClick={handleCreate}
									>
										<i className="fas fa-plus mr-2"></i>
										Create New Template
									</button>
								)} */}
							</div>
						</div>
					</div>
				</div>
			</div>

			<section className="content">
				<div className="container-fluid">
					<div className="card">
						<div className="card-header">
							<h3 className="card-title">Templates List</h3>
						</div>

						<div className="card-body">
							<div className="row mb-3">
								<div className="col-sm-3">
									<select
										className="form-control"
										value={filterType}
										onChange={(e) => {
											setFilterType(e.target.value);
											setCurrentPage(1);
										}}
									>
										<option value="">Search By Type</option>
										{templateTypes.map((type) => (
											<option key={type.id} value={type.slug}>
												{type.name}
											</option>
										))}
									</select>
								</div>
								<div className="col-sm-3">
									<input
										type="text"
										className="form-control"
										placeholder="Search by title..."
										value={filterTitle}
										onChange={(e) => {
											setFilterTitle(e.target.value);
											setCurrentPage(1);
										}}
									/>
								</div>
								<div className="col-sm-3">
									<select
										className="form-control"
										value={filterActive === undefined ? '' : filterActive ? 'true' : 'false'}
										onChange={(e) => {
											setFilterActive(e.target.value === '' ? undefined : e.target.value === 'true');
											setCurrentPage(1);
										}}
									>
										<option value="">Search By Status</option>
										<option value="true">Active</option>
										<option value="false">Inactive</option>
									</select>
								</div>
								<div className="col-sm-3">
									<button
										type="button"
										className="btn btn-secondary"
										onClick={handleResetFilters}
									>
										<i className="fas fa-redo mr-2"></i>
										Reset Filters
									</button>
								</div>
							</div>

							<div className="table-responsive">
								{loading ? (
									<div className="text-center p-4">
										<div className="spinner-border" role="status">
											<span className="sr-only">Loading...</span>
										</div>
									</div>
								) : templates.length === 0 ? (
									<div className="text-center p-4">
										<p className="text-muted">No templates found</p>
									</div>
								) : (
									<table className="table table-hover">
										<thead>
											<tr>
												<th style={{ width: '120px' }}>Type</th>
												<th>Title</th>
												<th style={{ width: '100px' }}>Owner</th>
												<th style={{ width: '100px' }}>Version</th>
												<th style={{ width: '100px' }}>Status</th>
												<th style={{ width: '150px' }}>Updated</th>
												<th style={{ width: '200px' }}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{templates.map((template) => (
												<tr key={template.id}>
													<td>
														<span className={`badge badge-${getTypeBadgeColor(template.type?.toString() || '')}`}>
															{(template as any).type_name || template.type?.toString().replace('_', ' ') || 'N/A'}
														</span>
													</td>
													<td>{template.title}</td>
													<td>
														<span className={`badge badge-${(template as any).template_owner_type === 'franchise' ? 'info' : 'secondary'}`}>
															{(template as any).template_owner_type === 'franchise' ? 'Franchise' : 'Default'}
														</span>
													</td>
													<td>{template.version}</td>
													<td>
														<span className={`badge badge-${template.is_active ? 'success' : 'secondary'}`}>
															{template.is_active ? 'Active' : 'Inactive'}
														</span>
													</td>
													<td>
														{template.updated_at
															? moment(template.updated_at).format('DD/MM/YYYY')
															: '-'}
													</td>
													<td>
														<div className="btn-group btn-group-sm">
															<button
																type="button"
																className="btn btn-secondary"
																onClick={() => handlePreview(template)}
																title="Preview Template"
															>
																<i className="fas fa-eye"></i>
															</button>
															<button
																type="button"
																className="btn btn-dark"
																onClick={() => handleViewHistory(template)}
																title="View History"
																// disabled={template.template_owner_type === 'default'}
															>
																<i className="fas fa-history"></i>
															</button>
															{(permissions?.['edit-content'] || permissions?.['edit-values']) && (
																<button
																	type="button"
																	className="btn btn-info"
																	onClick={() => handleEdit(template, 'content')}
																	title="Edit Template"
																>
																	<i className="fas fa-edit"></i>
																</button>
															)}
															{permissions?.['edit-content'] && (
																<button
																	type="button"
																	className={`btn btn-${template.is_active ? 'warning' : 'success'}`}
																	onClick={() => handleToggleStatus(template.id!, template.is_active)}
																	title={template.is_active ? 'Deactivate' : 'Activate'}
																	disabled={template.template_owner_type === 'default'}
																>
																	<i className={`fas fa-${template.is_active ? 'toggle-on' : 'toggle-off'}`}></i>
																</button>
															)}
															{/* {permissions?.['create-templates'] && (
																<button
																	type="button"
																	className="btn btn-primary"
																	onClick={() => handleDuplicate(template.id!, template.title)}
																	title="Duplicate"
																>
																	<i className="fas fa-copy"></i>
																</button>
															)}
															{permissions?.['edit-content'] && (
																<button
																	type="button"
																	className="btn btn-danger"
																	onClick={() => handleDelete(template.id!)}
																	title="Delete"
																>
																	<i className="fas fa-trash"></i>
																</button>
															)} */}
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>
						</div>

						{totalPages > 1 && (
							<div className="card-footer clearfix">
								<ul className="pagination pagination-sm m-0 float-right">
									<li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
										<button
											className="page-link"
											onClick={() => setCurrentPage(currentPage - 1)}
											disabled={currentPage === 1}
										>
											«
										</button>
									</li>
									{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
										<li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
											<button className="page-link" onClick={() => setCurrentPage(page)}>
												{page}
											</button>
										</li>
									))}
									<li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
										<button
											className="page-link"
											onClick={() => setCurrentPage(currentPage + 1)}
											disabled={currentPage === totalPages}
										>
											»
										</button>
									</li>
								</ul>
							</div>
						)}
					</div>
				</div>
			</section>
		</div>
	);
};

export default ManageContentTemplates;